import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import AppleLogin from "react-apple-login";
import "./styles.css";
import Logo from "../../assets/Logo.jpeg";

const EventLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authProvider, setAuthProvider] = useState(null);

  const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID;
  const REDIRECT_URI =
    process.env.REACT_APP_REDIRECT_URI ||
    window.location.origin + "/events/auth/callback";
  const BACKEND_URL =
    process.env.REACT_APP_BACKEND_URL ||
    "https://ota-backend.altariumconsulting.com";

  useEffect(() => {
    const token = localStorage.getItem("JWTtoken");
    if (token) {
      navigate("/events/login");
    }
  }, []);

  const sendTokenToBackend = async (idToken, provider) => {
    try {
      setLoading(true);
      setAuthProvider(provider);

      const response = await fetch(`${BACKEND_URL}/${provider}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: idToken,
        }),
      });

      if (!response.ok) {
        throw new Error("Backend authentication failed");
      }

      const data = await response.json();

      localStorage.setItem("JWTtoken", data.token);
      localStorage.setItem("userId", data.user.cuid);

      await checkUserProfile(data.user.cuid, data.token);
    } catch (error) {
      console.error(`${provider} authentication error:`, error);
      setError(
        `${
          provider.charAt(0).toUpperCase() + provider.slice(1)
        } authentication failed`
      );
    } finally {
      setLoading(false);
      setAuthProvider(null);
    }
  };

  const handleGoogleSuccess = (credentialResponse) => {
    sendTokenToBackend(credentialResponse.credential, "google");
  };

  const handleGoogleError = () => {
    setError("Google authentication failed. Please try again.");
    setLoading(false);
    setAuthProvider(null);
  };

  const handleAppleSuccess = (response) => {
    sendTokenToBackend(response.authorization.id_token, "apple");
  };

  const handleAppleError = (error) => {
    console.error("Apple authentication error:", error);
    setError("Apple authentication failed. Please try again.");
    setLoading(false);
    setAuthProvider(null);
  };

  const checkUserProfile = async (userId, token) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/auth/getuserprod?cuid=${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const userData = await response.json();

      navigate("/events/checkout");
    } catch (error) {
      console.error("Error checking user profile:", error);
      navigate("/events/login");
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="logo">
            <img src={Logo} alt="Logo" />
          </div>
          <h1 className="login-title">Welcome To Ota Life</h1>
          <p className="login-subtitle">Sign in to continue to your account</p>
        </div>

        {error && (
          <div className="error-banner">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                fill="currentColor"
              />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <div className="login-buttons">
          <div className="google-login-wrapper">
            {loading && authProvider === "google" ? (
              <button className="login-btn google-btn" disabled>
                <div className="btn-loader"></div>
              </button>
            ) : (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
                theme="outline"
                size="large"
                text="continue_with"
                shape="rectangular"
                logo_alignment="left"
              />
            )}
          </div>

          {loading && authProvider === "apple" ? (
            <button className="login-btn apple-btn" disabled>
              <div className="btn-loader"></div>
            </button>
          ) : (
            <AppleLogin
              clientId={APPLE_CLIENT_ID}
              redirectURI={REDIRECT_URI}
              scope="email name"
              responseMode="form_post"
              usePopup={true}
              callback={handleAppleSuccess}
              onError={handleAppleError}
              render={(props) => (
                <button {...props} className="login-btn apple-btn">
                  <svg
                    className="btn-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  <span>Continue with Apple</span>
                </button>
              )}
            />
          )}
        </div>
      </div>

      <div className="login-background">
        <div className="bg-shape shape-1"></div>
        <div className="bg-shape shape-2"></div>
        <div className="bg-shape shape-3"></div>
      </div>
    </div>
  );
};

export default EventLogin;
